import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import type { LoginResponse, Principal } from '@dru/types';
import { api, } from './api';
import { ApiError } from '@dru/api-client';

/**
 * Session state.
 *
 * The access token lives in memory and nowhere else. On a reload it is gone,
 * and the app silently re-establishes it from the HttpOnly refresh cookie —
 * which is why `bootstrapping` exists: without it the router would flash the
 * login screen at a user who is, in fact, signed in.
 *
 * Permissions come from /auth/me rather than from the token the client
 * decoded, so what the UI shows and what the guards enforce derive from the
 * same source.
 */

/**
 * What a sign-in attempt produced.
 *
 * `login` cannot simply return a principal any more: on an account with a
 * second factor the password step ends in a challenge and there is no
 * principal yet. Returning `null` for that case would be indistinguishable
 * from a failure, so the two outcomes are named.
 */
export type LoginOutcome =
  | { kind: 'session'; principal: Principal | null }
  | { kind: 'mfa'; challengeToken: string; expiresIn: number };

interface AuthState {
  principal: Principal | null;
  bootstrapping: boolean;
  permissions: ReadonlySet<string>;
  /** Either a session, or a demand for the code. */
  login(email: string, password: string, rememberMe?: boolean): Promise<LoginOutcome>;
  /** Second half of the sign-in, once the person has read their code. */
  verifyMfa(challengeToken: string, code: string, rememberMe?: boolean): Promise<Principal | null>;
  logout(): Promise<void>;
  /**
   * Swap this tab into an impersonated session. The administrator's own
   * token is kept in memory so "end" can call the end endpoint as them — an
   * impersonated token is read-only and could not.
   */
  impersonate(accessToken: string): Promise<Principal | null>;
  /** Back to the administrator, ending the session on the server. */
  endImpersonation(): Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const me = await api.get<Principal>('/auth/me');
      setPrincipal(me);
      return me;
    } catch {
      setPrincipal(null);
      return null;
    }
  }, []);

  // On first paint, try to resume. The refresh cookie survives a reload even
  // though the access token does not.
  useEffect(() => {
    (async () => {
      try {
        // Through the client, which de-duplicates concurrent rotations. A raw
        // fetch here skipped that, so a bootstrap rotation racing a console
        // screen's first 401 presented the same refresh token twice — and
        // reuse detection reads a replay as theft and revokes the family.
        if (await api.resumeSession()) await loadMe();
      } catch {
        // No session. The router will send us to /login.
      } finally {
        setBootstrapping(false);
      }
    })();
  }, [loadMe]);

  // The client clears the token when a refresh finally fails; drop the
  // principal with it so the app cannot render a stale identity.
  useEffect(() => api.onTokenChange((t) => { if (!t) setPrincipal(null); }), []);

  const login = useCallback(async (
    email: string, password: string, rememberMe = false,
  ): Promise<LoginOutcome> => {
    const res = await api.post<LoginResponse>('/auth/login', { email, password, rememberMe });
    if (res.mfaRequired) {
      // No token to hold: the password step deliberately opened no session.
      return { kind: 'mfa', challengeToken: res.challengeToken, expiresIn: res.expiresIn };
    }
    api.setToken(res.accessToken);
    return { kind: 'session', principal: await loadMe() };
  }, [loadMe]);

  const verifyMfa = useCallback(async (challengeToken: string, code: string, rememberMe = false) => {
    const res = await api.post<LoginResponse>('/auth/mfa/verify', { challengeToken, code, rememberMe });
    // The endpoint only ever answers with a session; the guard is for the type.
    if (res.mfaRequired) throw new Error('Verification did not complete.');
    api.setToken(res.accessToken);
    return loadMe();
  }, [loadMe]);

  const logout = useCallback(async () => {
    // Best-effort: the server revokes the session, but a network failure must
    // not leave the user staring at a screen they think they left.
    await api.post('/auth/logout').catch(() => undefined);
    api.setToken(null);
    setPrincipal(null);
  }, []);

  // The administrator's own token while they are looking through somebody
  // else's eyes. Memory only, like every token here; a reload drops both and
  // the refresh cookie restores the administrator, never the target.
  const heldToken = useRef<string | null>(null);

  const impersonate = useCallback(async (accessToken: string) => {
    heldToken.current = api.getToken();
    api.setToken(accessToken);
    return loadMe();
  }, [loadMe]);

  const endImpersonation = useCallback(async () => {
    const sessionId = principal?.impersonation?.sessionId;
    if (heldToken.current) {
      api.setToken(heldToken.current);
      heldToken.current = null;
      if (sessionId) await api.post(`/admin/impersonation/${sessionId}/end`, {}).catch(() => undefined);
      await loadMe();
      return;
    }
    // No held token (the tab was reloaded): the refresh cookie belongs to the
    // administrator, so resuming is the way back.
    api.setToken(null);
    if (await api.resumeSession()) {
      if (sessionId) await api.post(`/admin/impersonation/${sessionId}/end`, {}).catch(() => undefined);
      await loadMe();
    } else {
      setPrincipal(null);
    }
  }, [principal?.impersonation?.sessionId, loadMe]);

  const value = useMemo<AuthState>(() => ({
    principal,
    bootstrapping,
    permissions: new Set(principal?.permissions ?? []),
    login,
    verifyMfa,
    logout,
    impersonate,
    endImpersonation,
  }), [principal, bootstrapping, login, verifyMfa, logout, impersonate, endImpersonation]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** Turns an unknown throw into something worth showing a person. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.userMessage;
  if (e instanceof Error) return e.message;
  return 'Something went wrong.';
}
